/**
 * Frontend Types for Biweekly Rotation
 * Simplified type definitions matching the new backend API
 */

/**
 * Execution mode for biweekly rotation
 */
export type RotationExecutionMode = 'AUTOMATIC' | 'MANUAL' | 'PREVIEW';

/**
 * Rotation status
 */
export type RotationStatus = 'SCHEDULED' | 'EXECUTING' | 'EXECUTED' | 'SKIPPED' | 'FAILED';

/**
 * Schedule configuration
 */
export interface RotationSchedule {
  cadenceDays: number;
  dayOfWeek: string;
  executionTime: string;
  timezone: string;
}

/**
 * Risk limits
 */
export interface RotationLimits {
  maxTurnoverPct: number;
  maxCostBps: number;
  minCashBufferPct: number;
  maxGrossExposurePct: number;
}

/**
 * Full rotation configuration
 */
export interface RotationConfig {
  executionMode: RotationExecutionMode;
  executionModeLabel: string;
  executionModeDescription?: string;
  schedule: RotationSchedule;
  limits: RotationLimits;
  isActive: boolean;
}

/**
 * Action in a rotation plan
 */
export interface RotationAction {
  type: 'EXIT' | 'TRIM' | 'ADD' | 'REFILL' | 'HEDGE' | 'UNHEDGE';
  symbol: string;
  changeShares: number;
  reason: string;
  sector?: string;
  currentShares?: number;
  targetShares?: number;
  currentValue?: number;
  targetValue?: number;
}

/**
 * Portfolio metrics
 */
export interface PortfolioMetrics {
  totalValue: number;
  cashBuffer: number;
  cashBufferPct: number;
  netExposure: number;
  netExposurePct: number;
  grossExposure: number;
  grossExposurePct: number;
  positionCount: number;
  sectorWeights: Record<string, number>;
}

/**
 * Individual risk check
 */
export interface RiskCheck {
  name: string;
  passed: boolean;
  currentValue: number;
  limit: number;
  message: string;
}

/**
 * Risk check results
 */
export interface RiskCheckResults {
  allPassed: boolean;
  checks: RiskCheck[];
}

/**
 * Overview data from /overview endpoint
 */
export interface RotationOverview {
  status: {
    isActive: boolean;
    executionMode: RotationExecutionMode;
    executionModeLabel: string;
    nextRotationDate: string;
    nextRotationFormatted: string;
    timeUntilNext: {
      days: number;
      hours: number;
      minutes: number;
    };
  };
  lastRotation: {
    date: string;
    formattedDate: string;
    status: RotationStatus;
    summary: {
      totalExits: number;
      totalAdds: number;
      turnoverPct: number;
      costBps: number;
    };
  } | null;
  statistics: {
    totalRotationsLast30Days: number;
    avgTurnoverPct: number;
    avgCostBps: number;
    successRate: number;
  };
  schedule: RotationSchedule;
  limits: RotationLimits;
}

/**
 * Preview data from /preview endpoint
 */
export interface RotationPreview {
  hasActiveUsers: boolean;
  message?: string;
  scheduledFor: string | null;
  formattedDate?: string;
  timeUntil?: {
    days: number;
    hours: number;
    minutes: number;
  };
  actions: RotationAction[];
  summary: {
    totalExits: number;
    totalAdds: number;
    totalTrims: number;
    turnoverPct: number;
    estimatedCostBps: number;
  };
  currentPortfolio: PortfolioMetrics | null;
  projectedPortfolio: PortfolioMetrics | null;
  riskChecks: RiskCheckResults;
  canExecute: boolean;
  executionBlockedReason?: string;
}

/**
 * User in a rotation
 */
export interface RotationUser {
  id: string;
  email: string;
  name: string;
  planId: string;
  exits: number;
  adds: number;
  turnoverPct: number;
  costBps: number;
}

/**
 * Timeline entry from /timeline endpoint
 */
export interface TimelineEntry {
  id: string;
  date: string;
  formattedDate: string;
  status: RotationStatus;
  summary: {
    usersAffected: number;
    totalExits: number;
    totalAdds: number;
    avgTurnoverPct: number;
    avgCostBps: number;
  };
  users?: RotationUser[];
}

/**
 * Timeline response
 */
export interface TimelineResponse {
  timeline: TimelineEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * Helper to get execution mode styling
 */
export function getExecutionModeStyle(mode: RotationExecutionMode): {
  bgColor: string;
  textColor: string;
  borderColor: string;
} {
  switch (mode) {
    case 'AUTOMATIC':
      return {
        bgColor: 'bg-emerald-500/20',
        textColor: 'text-emerald-100',
        borderColor: 'border-emerald-500/30',
      };
    case 'MANUAL':
      return {
        bgColor: 'bg-yellow-500/20',
        textColor: 'text-yellow-100',
        borderColor: 'border-yellow-500/30',
      };
    case 'PREVIEW':
      return {
        bgColor: 'bg-blue-500/20',
        textColor: 'text-blue-100',
        borderColor: 'border-blue-500/30',
      };
  }
}

/**
 * Helper to format time until
 */
export function formatTimeUntil(timeUntil: { days: number; hours: number; minutes: number }): string {
  const parts: string[] = [];
  if (timeUntil.days > 0) {
    parts.push(`${timeUntil.days}d`);
  }
  if (timeUntil.hours > 0) {
    parts.push(`${timeUntil.hours}h`);
  }
  if (timeUntil.minutes > 0 && timeUntil.days === 0) {
    parts.push(`${timeUntil.minutes}m`);
  }
  return parts.join(' ') || 'now';
}

/**
 * Helper to format percentage
 */
export function formatPct(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Helper to format currency
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
